import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import { Wrench, ArrowLeft, AlertCircle, Clock, Bus as BusIcon, FileText, User, CheckCircle, Save, History, ClipboardList, DollarSign, LogOut, Edit, Plus, Calendar, Trash2, Gauge } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useModules } from '@/contexts/ModulesContext';

import { Textarea } from './ui/textarea';
import { TimeInput24h } from './ui/time-input-24h';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Task card component (reused across tabs)
function TaskCard({ task, onClick, showEarnings = false, onEdit, onDelete }) {
  const completedCount = (task.completed_parameter_ids || []).length;
  const totalCount = (task.parameters || []).length;
  const isCompleted = task.status === 'completed';

  return (
    <Card
      className={`overflow-hidden bg-white shadow-lg transition-all ${
        !isCompleted ? 'hover:shadow-xl cursor-pointer group' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      data-testid={`task-${task.id}`}
      onClick={onClick}
    >
      <div className={`px-4 py-2.5 ${
        isCompleted
          ? 'bg-gradient-to-r from-green-500 to-green-600'
          : task.is_important
          ? 'bg-gradient-to-r from-red-500 to-red-600'
          : 'bg-gradient-to-r from-orange-500 to-orange-600'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BusIcon className="w-4 h-4 text-white" />
            <h3 className="text-white font-bold text-sm">{task.bus_name || 'Без автобуса'}</h3>
            {task.bus_license_plate && <span className="text-white/70 text-xs font-mono">{task.bus_license_plate}</span>}
          </div>
          <div className="flex items-center gap-2">
            {task.is_important && (
              <span className="px-1.5 py-0.5 bg-white/25 rounded text-[10px] text-white font-bold">ВАЖНО</span>
            )}
            <span className="text-white/80 text-xs">
              {completedCount}/{totalCount}
            </span>
            <span className="text-white/60 text-xs">
              {new Date(task.created_at).toLocaleDateString('ru-RU')}
            </span>
            {onEdit && (
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                className="p-1 rounded hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                data-testid={`edit-task-${task.id}`}
              >
                <Edit className="w-3.5 h-3.5" />
              </button>
            )}
            {onDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(task); }}
                className="p-1 rounded hover:bg-red-600/50 text-white/80 hover:text-white transition-colors"
                data-testid={`delete-task-${task.id}`}
                title="Удалить задачу"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <User className="w-3 h-3" />
          <span>Назначил: <strong className="text-gray-700">{task.assigned_by_name}</strong></span>
        </div>

        {/* Params */}
        {task.parameters && task.parameters.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {task.parameters.map((param, idx) => {
              const isDone = (task.completed_parameter_ids || []).includes(param.parameter_id);
              return (
                <span key={idx} className={`text-[11px] px-2 py-0.5 rounded-lg border font-medium ${
                  isDone
                    ? 'bg-green-50 text-green-700 border-green-200 line-through'
                    : param.is_critical
                    ? 'bg-red-50 text-red-700 border-red-200'
                    : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}>
                  {isDone && <CheckCircle className="w-3 h-3 inline mr-0.5" />}
                  {param.parameter_name}
                </span>
              );
            })}
          </div>
        )}

        {/* Admin note */}
        {task.admin_note && (
          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-800">
            <User className="w-3 h-3 inline mr-1" />
            {task.admin_note}
          </div>
        )}

        {/* Bus notes */}
        {task.bus_notes && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            <FileText className="w-3 h-3 inline mr-1" />
            {task.bus_notes}
          </div>
        )}

        {/* Mechanic note */}
        {task.mechanic_note && (
          <div className="bg-green-50 border border-green-200 rounded p-2 text-xs text-green-800">
            <Wrench className="w-3 h-3 inline mr-1" />
            {task.mechanic_note}
          </div>
        )}

        {/* Earnings */}
        {showEarnings && (task.dirty_hours > 0 || task.clean_hours > 0) && (
          <div className="flex flex-wrap gap-2 text-xs pt-1">
            {task.dirty_hours > 0 && (
              <span className="px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 rounded">
                Грязная: {task.dirty_hours}ч x {task.dirty_rate}€ = {task.dirty_payment}€
              </span>
            )}
            {task.clean_hours > 0 && (
              <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">
                Чистая: {task.clean_hours}ч x {task.clean_rate}€ = {task.clean_payment}€
              </span>
            )}
            {task.total_payment > 0 && (
              <span className="px-2 py-0.5 bg-green-50 text-green-800 border border-green-200 rounded font-bold">
                Итого: {task.total_payment}€
              </span>
            )}
          </div>
        )}

        {/* Mileage at completion */}
        {isCompleted && task.mileage_at_completion > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 pt-1">
            <Gauge className="w-3.5 h-3.5" />
            <span>Пробег при выполнении: <strong className="text-gray-700">{task.mileage_at_completion.toLocaleString()} км</strong></span>
          </div>
        )}

        {!isCompleted && (
          <p className="text-[10px] text-gray-400 group-hover:text-orange-500 transition-colors">
            Нажмите для выполнения
          </p>
        )}
      </div>
    </Card>
  );
}

// Earnings summary component
function EarningsSummary({ tasks, bonusesPenalties = [], dateFrom, dateTo }) {
  const totals = useMemo(() => {
    let dirtyH = 0, cleanH = 0, dirtyP = 0, cleanP = 0, totalP = 0;
    tasks.forEach(t => {
      dirtyH += t.dirty_hours || 0;
      cleanH += t.clean_hours || 0;
      dirtyP += t.dirty_payment || 0;
      cleanP += t.clean_payment || 0;
      totalP += t.total_payment || 0;
    });
    
    // Filter bonuses/penalties by date range if provided
    const filteredBP = bonusesPenalties.filter(bp => {
      if (!dateFrom && !dateTo) return true;
      const bpDate = (bp.date || bp.created_at || '').split('T')[0];
      if (dateFrom && bpDate < dateFrom) return false;
      if (dateTo && bpDate > dateTo) return false;
      return true;
    });
    
    const bonuses = filteredBP.filter(bp => bp.type === 'bonus').reduce((sum, bp) => sum + (bp.amount || 0), 0);
    const penalties = filteredBP.filter(bp => bp.type === 'penalty').reduce((sum, bp) => sum + (bp.amount || 0), 0);
    const toPay = totalP + bonuses - penalties;
    
    return {
      dirtyH: Math.round(dirtyH * 100) / 100,
      cleanH: Math.round(cleanH * 100) / 100,
      dirtyP: Math.round(dirtyP * 100) / 100,
      cleanP: Math.round(cleanP * 100) / 100,
      totalP: Math.round(totalP * 100) / 100,
      bonuses: Math.round(bonuses * 100) / 100,
      penalties: Math.round(penalties * 100) / 100,
      toPay: Math.round(toPay * 100) / 100,
      count: tasks.length
    };
  }, [tasks, bonusesPenalties, dateFrom, dateTo]);

  if (tasks.length === 0 && totals.bonuses === 0 && totals.penalties === 0) return null;

  return (
    <div className="space-y-3 mb-4" data-testid="earnings-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-3 bg-orange-50 border-orange-200">
          <p className="text-[10px] text-orange-600 font-medium">Грязная работа</p>
          <p className="text-lg font-bold text-orange-800">{totals.dirtyH}ч</p>
          <p className="text-xs text-orange-600">{totals.dirtyP}€</p>
        </Card>
        <Card className="p-3 bg-blue-50 border-blue-200">
          <p className="text-[10px] text-blue-600 font-medium">Чистая работа</p>
          <p className="text-lg font-bold text-blue-800">{totals.cleanH}ч</p>
          <p className="text-xs text-blue-600">{totals.cleanP}€</p>
        </Card>
        <Card className="p-3 bg-green-50 border-green-200">
          <p className="text-[10px] text-green-600 font-medium">Заработано</p>
          <p className="text-lg font-bold text-green-800">{totals.totalP}€</p>
          <p className="text-xs text-green-600">{totals.count} работ</p>
        </Card>
        <Card className="p-3 bg-gray-50 border-gray-200">
          <p className="text-[10px] text-gray-600 font-medium">Всего часов</p>
          <p className="text-lg font-bold text-gray-800">{Math.round((totals.dirtyH + totals.cleanH) * 100) / 100}ч</p>
          <p className="text-xs text-gray-500">грязн. + чист.</p>
        </Card>
      </div>
      
      {/* Total to pay with bonuses/penalties */}
      <Card className="p-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-white/70 font-medium">К ПОЛУЧЕНИЮ</p>
            <p className="text-3xl font-bold">{totals.toPay}€</p>
          </div>
          <div className="text-right text-sm">
            <div className="flex items-center justify-end gap-2">
              <span className="text-white/70">Заработано:</span>
              <span className="font-semibold">{totals.totalP}€</span>
            </div>
            {totals.bonuses > 0 && (
              <div className="flex items-center justify-end gap-2 text-green-300">
                <span>Премии:</span>
                <span className="font-semibold">+{totals.bonuses}€</span>
              </div>
            )}
            {totals.penalties > 0 && (
              <div className="flex items-center justify-end gap-2 text-red-300">
                <span>Вычеты:</span>
                <span className="font-semibold">-{totals.penalties}€</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

// Date filter bar for admin
// Фильтр для Истории: месяц, прошлый месяц, период
function HistoryFilterBar({ preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo, showCustom = true }) {
  const presets = [
    { key: 'month', label: 'Месяц' },
    { key: 'lastMonth', label: 'Прошлый месяц' },
    ...(showCustom ? [{ key: 'custom', label: 'Период' }] : []),
  ];

  return (
    <Card className="mb-4 p-3 bg-white shadow-md" data-testid="history-filter-bar">
      <div className="flex flex-wrap gap-2 items-center">
        {presets.map(p => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              preset === p.key
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${p.key}`}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && showCustom && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-sm"
              data-testid="filter-date-from"
            />
            <span className="text-gray-400 text-sm">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-sm"
              data-testid="filter-date-to"
            />
          </div>
        )}
      </div>
    </Card>
  );
}

// Фильтр для Отчётов: месяц, год, все, период (без полгода)
function ReportFilterBar({ preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo }) {
  const presets = [
    { key: 'month', label: 'Месяц' },
    { key: 'year', label: 'Год' },
    { key: 'all', label: 'Все' },
    { key: 'custom', label: 'Период' },
  ];

  return (
    <Card className="mb-4 p-3 bg-white shadow-md" data-testid="report-filter-bar">
      <div className="flex flex-wrap gap-2 items-center">
        {presets.map(p => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              preset === p.key
                ? 'bg-orange-500 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${p.key}`}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-sm"
              data-testid="filter-date-from"
            />
            <span className="text-gray-400 text-sm">—</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border rounded-lg px-2 py-1.5 text-sm"
              data-testid="filter-date-to"
            />
          </div>
        )}
      </div>
    </Card>
  );
}

// Full create task form for mechanic
function CreateTaskForm({ user, allBuses, onCreated, onCancel }) {
  const [busId, setBusId] = useState('');
  const [expiredParams, setExpiredParams] = useState([]);
  const [busNotes, setBusNotes] = useState('');
  const [selectedParams, setSelectedParams] = useState([]);
  const [mechNote, setMechNote] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [dStart, setDStart] = useState('');
  const [dEnd, setDEnd] = useState('');
  const [cStart, setCStart] = useState('');
  const [cEnd, setCEnd] = useState('');
  const [taskDate, setTaskDate] = useState(new Date().toISOString().split('T')[0]);
  const [loadingParams, setLoadingParams] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchBusParams = async (id) => {
    setLoadingParams(true);
    try {
      const res = await axios.get(`${API}/api/buses/${id}/expired-params`, getAuthHeaders());
      const params = res.data.expired_params || [];
      setExpiredParams(params);
      setBusNotes(res.data.bus_notes || '');
      setSelectedParams(params.map(p => p.parameter_id));
    } catch { setExpiredParams([]); }
    finally { setLoadingParams(false); }
  };

  const handleBusChange = (id) => {
    setBusId(id);
    if (id) fetchBusParams(id);
    else { setExpiredParams([]); setSelectedParams([]); }
  };

  const toggleParam = (pid) => {
    setSelectedParams(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      // Create task
      const res = await axios.post(`${API}/api/mechanic-tasks`, {
        bus_id: busId || null,
        mechanic_id: user.id,
        parameter_ids: selectedParams,
        admin_note: adminNote,
        mechanic_note: mechNote
      }, getAuthHeaders());

      // If has work hours, immediately complete
      const hasWork = (dStart && dEnd) || (cStart && cEnd);
      if (hasWork && res.data.task_id) {
        await axios.put(`${API}/api/mechanic-tasks/${res.data.task_id}/complete`, {
          completed_parameter_ids: selectedParams,
          mechanic_note: mechNote,
          bus_notes: busNotes,
          admin_note: adminNote,
          dirty_work_start: dStart || null,
          dirty_work_end: dEnd || null,
          clean_work_start: cStart || null,
          clean_work_end: cEnd || null
        }, getAuthHeaders());
      }

      toast.success('Работа создана');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания');
    } finally { setSending(false); }
  };

  const criticalParams = expiredParams.filter(p => p.is_critical);
  const nonCriticalParams = expiredParams.filter(p => !p.is_critical);

  return (
    <div className="flex-1 overflow-y-auto space-y-4 pr-1">
      {/* Bus selector */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-1.5 block">Автобус</label>
        <Select value={busId} onValueChange={handleBusChange}>
          <SelectTrigger data-testid="create-bus-select">
            <SelectValue placeholder="Выберите автобус..." />
          </SelectTrigger>
          <SelectContent>
            {allBuses.map(b => (
              <SelectItem key={b.id} value={b.id}>
                {b.brand} {b.model} — {b.license_plate}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Date */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
          <Clock className="w-4 h-4 text-gray-500" />
          Дата
        </label>
        <input type="date" value={taskDate} onChange={(e) => setTaskDate(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="create-date" />
      </div>

      {/* Bus params */}
      {busId && (
        <>
          {loadingParams ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          ) : (
            <>
              {/* Bus notes */}
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-amber-600" /> Заметки автобуса
                </label>
                <textarea value={busNotes} onChange={(e) => setBusNotes(e.target.value)}
                  placeholder="Заметки по автобусу..."
                  className="w-full border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-sm resize-none" rows={2} />
              </div>

              {/* Parameters */}
              {expiredParams.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600 font-medium">
                      Параметры: {selectedParams.length} из {expiredParams.length}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setSelectedParams(expiredParams.map(p => p.parameter_id))}>Все</Button>
                      <Button variant="outline" size="sm" onClick={() => setSelectedParams([])}>Снять</Button>
                    </div>
                  </div>
                  {criticalParams.map(p => (
                    <label key={p.parameter_id} className={`flex items-center gap-3 p-2.5 rounded-lg border-2 cursor-pointer mb-1.5 transition-all ${
                      selectedParams.includes(p.parameter_id) ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white'}`}>
                      <input type="checkbox" checked={selectedParams.includes(p.parameter_id)} onChange={() => toggleParam(p.parameter_id)} className="w-4 h-4 text-red-600 rounded" />
                      <span className="font-medium text-sm">{p.parameter_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded-full">{p.status_text}</span>
                    </label>
                  ))}
                  {nonCriticalParams.map(p => (
                    <label key={p.parameter_id} className={`flex items-center gap-3 p-2.5 rounded-lg border-2 cursor-pointer mb-1.5 transition-all ${
                      selectedParams.includes(p.parameter_id) ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'}`}>
                      <input type="checkbox" checked={selectedParams.includes(p.parameter_id)} onChange={() => toggleParam(p.parameter_id)} className="w-4 h-4 text-orange-600 rounded" />
                      <span className="font-medium text-sm">{p.parameter_name}</span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{p.status_text}</span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Mechanic note */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
          <Wrench className="w-4 h-4 text-green-600" /> Заметка механика
        </label>
        <textarea value={mechNote} onChange={(e) => setMechNote(e.target.value)}
          placeholder="Заметка по работе..."
          className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} data-testid="create-mech-note" />
      </div>

      {/* Admin note */}
      <div>
        <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
          <User className="w-4 h-4 text-blue-600" /> Важная работа
        </label>
        <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
          placeholder="Комментарий (необязательно)"
          className="w-full border rounded-lg px-3 py-2 text-sm resize-none" rows={2} data-testid="create-admin-note" />
      </div>

      {/* Work hours */}
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-700">Время работы:</p>
        <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="text-sm font-bold text-orange-800">Грязная работа</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 w-6">с</span>
            <TimeInput24h value={dStart} onChange={setDStart} data-testid="create-dirty-start" />
            <span className="text-xs text-gray-600 w-8 text-center">по</span>
            <TimeInput24h value={dEnd} onChange={setDEnd} data-testid="create-dirty-end" />
          </div>
        </div>
        <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-sm font-bold text-blue-800">Чистая работа</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 w-6">с</span>
            <TimeInput24h value={cStart} onChange={setCStart} data-testid="create-clean-start" />
            <span className="text-xs text-gray-600 w-8 text-center">по</span>
            <TimeInput24h value={cEnd} onChange={setCEnd} data-testid="create-clean-end" />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-3 border-t">
        <Button variant="outline" onClick={onCancel} className="flex-1">Отмена</Button>
        <Button
          onClick={handleSubmit}
          disabled={sending}
          className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="create-task-submit"
        >
          {sending ? 'Создание...' : <><Save className="w-4 h-4 mr-2" />Создать</>}
        </Button>
      </div>
    </div>
  );
}

// Edit task dialog for admin/mechanic
function EditTaskDialog({ task, open, onOpenChange, onSaved }) {
  const [adminNote, setAdminNote] = useState(task?.admin_note || '');
  const [mechNote, setMechNote] = useState(task?.mechanic_note || '');
  const [busNotes, setBusNotes] = useState(task?.bus_notes || '');
  const [taskDate, setTaskDate] = useState('');
  const [dStart, setDStart] = useState(task?.dirty_work_start || '');
  const [dEnd, setDEnd] = useState(task?.dirty_work_end || '');
  const [cStart, setCStart] = useState(task?.clean_work_start || '');
  const [cEnd, setCEnd] = useState(task?.clean_work_end || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setAdminNote(task.admin_note || '');
      setMechNote(task.mechanic_note || '');
      setBusNotes(task.bus_notes || '');
      setTaskDate((task.completed_at || task.created_at || '').split('T')[0]);
      setDStart(task.dirty_work_start || '');
      setDEnd(task.dirty_work_end || '');
      setCStart(task.clean_work_start || '');
      setCEnd(task.clean_work_end || '');
    }
  }, [task]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        admin_note: adminNote,
        mechanic_note: mechNote,
        bus_notes: busNotes,
        dirty_work_start: dStart || null,
        dirty_work_end: dEnd || null,
        clean_work_start: cStart || null,
        clean_work_end: cEnd || null
      };
      // Send date update
      if (taskDate) {
        const origDate = (task.completed_at || task.created_at || '').split('T')[0];
        if (taskDate !== origDate) {
          if (task.completed_at) payload.completed_at = taskDate + 'T' + (task.completed_at.split('T')[1] || '00:00:00');
          payload.created_at = taskDate + 'T' + (task.created_at.split('T')[1] || '00:00:00');
        }
      }
      await axios.put(`${API}/api/mechanic-tasks/${task.id}/edit`, payload, getAuthHeaders());
      toast.success('Работа обновлена');
      onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-task-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            Редактирование работы
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Bus info */}
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-2.5">
            <BusIcon className="w-4 h-4" />
            <span className="font-semibold">{task.bus_name}</span>
            <span className="text-xs font-mono text-gray-400">{task.bus_license_plate}</span>
          </div>

          {/* Date */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-gray-500" />
              Дата
            </label>
            <input
              type="date"
              value={taskDate}
              onChange={(e) => setTaskDate(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="edit-task-date"
            />
          </div>

          {/* Mechanic note */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-green-600" />
              Заметка механика
            </label>
            <Textarea
              data-testid="edit-mechanic-note"
              value={mechNote}
              onChange={(e) => setMechNote(e.target.value)}
              placeholder="Заметка механика..."
              rows={2}
            />
          </div>

          {/* Bus notes */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-amber-600" />
              Заметки автобуса
            </label>
            <Textarea
              data-testid="edit-bus-notes"
              value={busNotes}
              onChange={(e) => setBusNotes(e.target.value)}
              placeholder="Заметки по автобусу..."
              rows={2}
              className="border-amber-200 bg-amber-50"
            />
          </div>

          {/* Admin note */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-1.5">
              <User className="w-4 h-4 text-blue-600" />
              Важная работа
            </label>
            <p className="text-xs text-gray-400 mb-1.5">Если заполнить — работа станет "важной" и попадёт в отчёт</p>
            <Textarea
              data-testid="edit-admin-note"
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              placeholder="Важная работа..."
              rows={2}
            />
          </div>

          {/* Work hours */}
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">Время работы:</p>
            <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-sm font-bold text-orange-800">Грязная работа</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-6">с</span>
                <TimeInput24h value={dStart} onChange={setDStart} data-testid="edit-dirty-start" />
                <span className="text-xs text-gray-600 w-8 text-center">по</span>
                <TimeInput24h value={dEnd} onChange={setDEnd} data-testid="edit-dirty-end" />
              </div>
            </div>
            <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm font-bold text-blue-800">Чистая работа</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 w-6">с</span>
                <TimeInput24h value={cStart} onChange={setCStart} data-testid="edit-clean-start" />
                <span className="text-xs text-gray-600 w-8 text-center">по</span>
                <TimeInput24h value={cEnd} onChange={setCEnd} data-testid="edit-clean-end" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-3 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">Отмена</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="edit-task-save-btn"
            >
              {saving ? 'Сохранение...' : <><Save className="w-4 h-4 mr-2" />Сохранить</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MechanicPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');

  // Dialog state
  const [selectedTask, setSelectedTask] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [completedParams, setCompletedParams] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirtyStart, setDirtyStart] = useState('');
  const [dirtyEnd, setDirtyEnd] = useState('');
  const [cleanStart, setCleanStart] = useState('');
  const [cleanEnd, setCleanEnd] = useState('');
  const [mechanicNote, setMechanicNote] = useState('');
  const [taskBusNotes, setTaskBusNotes] = useState('');
  const [taskAdminNote, setTaskAdminNote] = useState('');
  const [taskDate, setTaskDate] = useState(''); // Дата заявки
  const [paramDescriptions, setParamDescriptions] = useState({}); // Описания параметров
  const [paramEffectiveDates, setParamEffectiveDates] = useState({}); // Даты вступления в силу для параметров по датам
  const [editTask, setEditTask] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Date filter state
  const [filterPreset, setFilterPreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Create task state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [allBuses, setAllBuses] = useState([]);
  const [selectedBusFilter, setSelectedBusFilter] = useState('all'); // Фильтр по автобусам
  const [bonusesPenalties, setBonusesPenalties] = useState([]); // Премии и вычеты

  const getFilterDates = () => {
    if (filterPreset === 'all') return { from: null, to: null };
    if (filterPreset === 'custom') return { from: dateFrom, to: dateTo };
    const now = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    
    let from, to;
    switch (filterPreset) {
      case 'month': 
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'year': 
        from = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      default: return { from: null, to: null };
    }
    return { from: fmt(from), to: fmt(to) };
  };

  useEffect(() => { fetchTasks(); fetchBuses(); fetchBonusesPenalties(); }, []);

  const fetchBonusesPenalties = async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get(`${API}/api/bonuses-penalties?user_id=${user.id}`, getAuthHeaders());
      setBonusesPenalties(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchBuses = async () => {
    try {
      const res = await axios.get(`${API}/api/buses`, getAuthHeaders());
      setAllBuses(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchTasks = async () => {
    try {
      const url = user?.role === 'admin'
        ? `${API}/api/mechanic-tasks`
        : `${API}/api/mechanic-tasks?mechanic_id=${user?.id}`;
      const response = await axios.get(url, getAuthHeaders());
      setAllTasks(response.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Ошибка загрузки задач');
    } finally {
      setLoading(false);
    }
  };

  // Filtered lists
  const activeTasks = useMemo(() => allTasks.filter(t => t.status === 'assigned'), [allTasks]);

  const filteredCompleted = useMemo(() => {
    const completed = allTasks.filter(t => t.status === 'completed');
    const { from, to } = getFilterDates();
    if (!from && !to) return completed;
    return completed.filter(t => {
      const d = (t.completed_at || t.created_at || '').split('T')[0];
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [allTasks, filterPreset, dateFrom, dateTo]);

  const filteredImportant = useMemo(() => {
    return filteredCompleted.filter(t => t.is_important);
  }, [filteredCompleted]);

  // Filtered report - with bus filter, no earnings
  const filteredReport = useMemo(() => {
    let tasks = filteredCompleted.filter(t => t.is_important);
    if (selectedBusFilter !== 'all') {
      tasks = tasks.filter(t => t.bus_id === selectedBusFilter);
    }
    return tasks;
  }, [filteredCompleted, selectedBusFilter]);

  // Delete task (admin only)
  const handleDeleteTask = async (task) => {
    if (!window.confirm(`Удалить задачу для ${task.bus_name}?`)) return;
    
    try {
      await axios.delete(`${API}/api/mechanic-tasks/${task.id}`, getAuthHeaders());
      toast.success('Задача удалена');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Ошибка удаления');
    }
  };

  const openTaskDialog = (task) => {
    if (task.status !== 'assigned') return;
    setSelectedTask(task);
    setCompletedParams([]);
    setDirtyStart('');
    setDirtyEnd('');
    setCleanStart('');
    setCleanEnd('');
    setMechanicNote('');
    setTaskBusNotes(task.bus_notes || '');
    setTaskAdminNote(task.admin_note || '');
    // Устанавливаем текущую дату как дату заявки по умолчанию
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
    setTaskDate(todayStr);
    // Инициализируем описания из параметров
    const descriptions = {};
    (task.parameters || []).forEach(p => {
      descriptions[p.parameter_id] = p.description || '';
    });
    setParamDescriptions(descriptions);
    // Инициализируем даты вступления в силу (пустые по умолчанию)
    setParamEffectiveDates({});
    setDialogOpen(true);
  };

  const toggleParam = (paramId) => {
    setCompletedParams(prev =>
      prev.includes(paramId) ? prev.filter(id => id !== paramId) : [...prev, paramId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await axios.put(`${API}/api/mechanic-tasks/${selectedTask.id}/complete`, {
        completed_parameter_ids: completedParams,
        mechanic_note: mechanicNote,
        bus_notes: taskBusNotes,
        admin_note: taskAdminNote,
        dirty_work_start: dirtyStart || null,
        dirty_work_end: dirtyEnd || null,
        clean_work_start: cleanStart || null,
        clean_work_end: cleanEnd || null,
        task_date: taskDate || null,
        parameter_descriptions: paramDescriptions,
        parameter_effective_dates: paramEffectiveDates
      }, getAuthHeaders());

      const data = result.data;
      const payInfo = data.total_payment > 0 ? ` Оплата: ${data.total_payment} EUR` : '';
      toast.success(`Работа завершена!${payInfo}`);
      setDialogOpen(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-slate-200 to-orange-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-orange-50" data-testid="mechanic-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 shadow-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-700">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Назад
              </Button>
              <div className="w-9 h-9 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-white">Механик</h1>
                <p className="text-[10px] text-slate-400">{user?.first_name || user?.username}</p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-red-400 hover:bg-slate-700"
              data-testid="mechanic-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-1" />
              Выйти
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full mb-4 bg-white shadow-md rounded-xl p-1 h-auto">
            <TabsTrigger
              value="tasks"
              className="py-2.5 text-sm font-medium data-[state=active]:bg-orange-500 data-[state=active]:text-white rounded-lg"
              data-testid="tab-tasks"
            >
              <Wrench className="w-4 h-4 mr-1.5" />
              Задачи
              {activeTasks.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-[10px]">{activeTasks.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="py-2.5 text-sm font-medium data-[state=active]:bg-green-500 data-[state=active]:text-white rounded-lg"
              data-testid="tab-history"
            >
              <History className="w-4 h-4 mr-1.5" />
              История
              {filteredCompleted.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-[10px]">{filteredCompleted.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="report"
              className="py-2.5 text-sm font-medium data-[state=active]:bg-red-500 data-[state=active]:text-white rounded-lg"
              data-testid="tab-report"
            >
              <ClipboardList className="w-4 h-4 mr-1.5" />
              Отчёт
              {filteredImportant.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded text-[10px]">{filteredImportant.length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Active tasks */}
          <TabsContent value="tasks">
            {/* Create task button */}
            <div className="mb-3">
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white w-full sm:w-auto"
                data-testid="create-task-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Создать работу
              </Button>
            </div>
            {activeTasks.length === 0 ? (
              <Card className="p-12 text-center bg-white shadow-lg">
                <Wrench className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-700 mb-1">Нет назначенных работ</h3>
                <p className="text-sm text-gray-500">Создайте работу или ждите назначения от администратора</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {activeTasks.map(task => (
                  <TaskCard key={task.id} task={task} onClick={() => openTaskDialog(task)} onDelete={isAdmin ? handleDeleteTask : undefined} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* History */}
          <TabsContent value="history">
            <HistoryFilterBar preset={filterPreset} setPreset={setFilterPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} showCustom={isAdmin} />
            <EarningsSummary 
              tasks={filteredCompleted} 
              bonusesPenalties={bonusesPenalties}
              dateFrom={getFilterDates().from}
              dateTo={getFilterDates().to}
            />
            {filteredCompleted.length === 0 ? (
              <Card className="p-12 text-center bg-white shadow-lg">
                <History className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-700 mb-1">История пуста</h3>
                <p className="text-sm text-gray-500">Завершённые работы будут отображаться здесь</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredCompleted.map(task => (
                  <TaskCard key={task.id} task={task} showEarnings onEdit={(t) => { setEditTask(t); setEditDialogOpen(true); }} onDelete={isAdmin ? handleDeleteTask : undefined} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Admin report (important only) */}
          <TabsContent value="report">
            <ReportFilterBar preset={filterPreset} setPreset={setFilterPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
            
            {/* Bus filter */}
            <div className="mb-4">
              <Select value={selectedBusFilter} onValueChange={setSelectedBusFilter}>
                <SelectTrigger className="w-full sm:w-64 bg-white" data-testid="bus-filter-select">
                  <BusIcon className="w-4 h-4 mr-2 text-gray-500" />
                  <SelectValue placeholder="Все автобусы" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все автобусы</SelectItem>
                  {allBuses.map(bus => (
                    <SelectItem key={bus.id} value={bus.id}>
                      {bus.brand} {bus.model} ({bus.license_plate})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {filteredReport.length === 0 ? (
              <Card className="p-12 text-center bg-white shadow-lg">
                <ClipboardList className="w-14 h-14 text-gray-300 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-gray-700 mb-1">Нет работ</h3>
                <p className="text-sm text-gray-500">Работы с заметкой администратора будут здесь</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredReport.map(task => (
                  <TaskCard key={task.id} task={task} showEarnings={false} onEdit={(t) => { setEditTask(t); setEditDialogOpen(true); }} onDelete={isAdmin ? handleDeleteTask : undefined} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Completion Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="mechanic-task-dialog">
          {selectedTask && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-orange-600" />
                  {selectedTask.bus_name}
                  <span className="text-sm font-normal text-gray-400 font-mono">{selectedTask.bus_license_plate}</span>
                </DialogTitle>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-3 pr-1">
                {/* Info */}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    <strong className="text-gray-700">{selectedTask.assigned_by_name}</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(selectedTask.created_at).toLocaleDateString('ru-RU')}
                  </span>
                </div>

                {/* Completion date */}
                <div>
                  <label className="text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-gray-500" />
                    Дата заявки
                  </label>
                  <input
                    type="date"
                    data-testid="task-date-input"
                    value={taskDate}
                    onChange={(e) => setTaskDate(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>

                {/* Bus notes - editable */}
                <div>
                  <label className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-amber-600" />
                    Заметки автобуса
                  </label>
                  <textarea
                    data-testid="bus-notes-input"
                    value={taskBusNotes}
                    onChange={(e) => setTaskBusNotes(e.target.value)}
                    placeholder="Заметки по автобусу..."
                    className="w-full border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
                    rows={2}
                  />
                </div>

                {/* Admin note - editable */}
                <div>
                  <label className="text-xs font-semibold text-blue-800 mb-1 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5 text-blue-600" />
                    Важная работа
                  </label>
                  <p className="text-[10px] text-gray-400 mb-1">Если заполнить — работа станет "важной" и попадёт в отчёт</p>
                  <textarea
                    data-testid="admin-note-input"
                    value={taskAdminNote}
                    onChange={(e) => setTaskAdminNote(e.target.value)}
                    placeholder="Важная работа..."
                    className="w-full border border-blue-200 bg-blue-50 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                    rows={2}
                  />
                </div>

                {/* Parameters to complete */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Отметьте выполненные:</p>
                  <div className="space-y-1.5">
                    {(selectedTask.parameters || []).map((param, idx) => {
                      const isChecked = completedParams.includes(param.parameter_id);
                      const isDateParam = param.tracking_type === 'date';
                      return (
                        <div
                          key={idx}
                          className={`p-2.5 rounded-lg border-2 transition-all ${
                            isChecked ? 'border-green-400 bg-green-50' : param.is_critical ? 'border-red-200 bg-red-50/30' : 'border-gray-200 bg-white'
                          }`}
                          data-testid={`complete-param-${param.parameter_id}`}
                        >
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" checked={isChecked} onChange={() => toggleParam(param.parameter_id)} className="w-5 h-5 mt-0.5 text-green-600 rounded" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-semibold text-sm ${isChecked ? 'text-green-700 line-through' : 'text-gray-900'}`}>{param.parameter_name}</span>
                                {param.is_critical && !isChecked && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-bold">КРИТИЧНО</span>}
                                {isChecked && <CheckCircle className="w-4 h-4 text-green-500" />}
                              </div>
                            </div>
                          </label>
                          <div className="flex gap-2 mt-2">
                            <input
                              type="text"
                              value={paramDescriptions[param.parameter_id] || ''}
                              onChange={(e) => setParamDescriptions(prev => ({
                                ...prev,
                                [param.parameter_id]: e.target.value
                              }))}
                              placeholder="Описание работы..."
                              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-orange-400"
                              onClick={(e) => e.stopPropagation()}
                            />
                            {isDateParam && (
                              <input
                                type="date"
                                value={paramEffectiveDates[param.parameter_id] || ''}
                                onChange={(e) => setParamEffectiveDates(prev => ({
                                  ...prev,
                                  [param.parameter_id]: e.target.value
                                }))}
                                title="Дата вступления в силу (если не указана - сразу)"
                                className="text-xs border border-blue-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400 bg-blue-50"
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Work time inputs */}
                <div className="pt-2 border-t space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Время работы:</p>
                  <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-orange-500" />
                      <span className="text-sm font-bold text-orange-800">Грязная работа</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-6">с</span>
                      <TimeInput24h value={dirtyStart} onChange={setDirtyStart} data-testid="dirty-work-start" />
                      <span className="text-xs text-gray-600 w-8 text-center">по</span>
                      <TimeInput24h value={dirtyEnd} onChange={setDirtyEnd} data-testid="dirty-work-end" />
                    </div>
                  </div>
                  <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500" />
                      <span className="text-sm font-bold text-blue-800">Чистая работа</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-6">с</span>
                      <TimeInput24h value={cleanStart} onChange={setCleanStart} data-testid="clean-work-start" />
                      <span className="text-xs text-gray-600 w-8 text-center">по</span>
                      <TimeInput24h value={cleanEnd} onChange={setCleanEnd} data-testid="clean-work-end" />
                    </div>
                  </div>
                </div>

                {/* Mechanic note */}
                <div className="pt-2 border-t">
                  <label className="text-sm font-semibold text-gray-700 mb-1.5 block">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Заметка механика
                  </label>
                  <textarea
                    data-testid="mechanic-note-input"
                    value={mechanicNote}
                    onChange={(e) => setMechanicNote(e.target.value)}
                    placeholder="Комментарий по работе (необязательно)"
                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows={2}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-3 border-t mt-1">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Отмена</Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold"
                  data-testid="save-completed-button"
                >
                  {saving ? 'Сохранение...' : (
                    <><Save className="w-4 h-4 mr-2" />Завершить работу</>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog (admin only) */}
      <EditTaskDialog
        task={editTask}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSaved={fetchTasks}
      />

      {/* Create Task Dialog — full form */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Plus className="w-5 h-5 text-orange-500" />
              Создать работу
            </DialogTitle>
          </DialogHeader>
          <CreateTaskForm
            user={user}
            allBuses={allBuses}
            onCreated={() => { setCreateDialogOpen(false); fetchTasks(); }}
            onCancel={() => setCreateDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
